import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import type { FilmStage, FilmStageCtor } from './film-stage';
import { FilmAction, FilmChapter, FilmEvent } from './film.types';

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/**
 * Player for a real-time 3D "film": the canvas, the chapter copy, the hotspot
 * labels and the timeline. The 3D scene itself is supplied by `loader`, which
 * is only called once the film is near the viewport — so three.js and the
 * scene code stay out of the initial page load.
 *
 * Sizing is set from outside with CSS custom properties on the element:
 * `--film-height`, `--film-min-height`, `--film-pad-top`, and
 * `--film-fallback` / `--film-fallback-tall` (the photo shown without WebGL).
 */
@Component({
  selector: 'app-film',
  imports: [NgTemplateOutlet],
  templateUrl: './film.html',
  styleUrl: './film.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Film {
  /** short id: becomes the `film--<name>` class and the `?<name>=chapter:progress` debug switch */
  readonly name = input.required<string>();
  /** accessible name of the section */
  readonly label = input.required<string>();
  /** id for in-page links */
  readonly anchor = input<string | null>(null);
  readonly chapters = input.required<FilmChapter[]>();
  /** imports the scene class — keep it a dynamic `import()` so it is code-split */
  readonly loader = input.required<() => Promise<FilmStageCtor>>();
  readonly actions = input<FilmAction[]>([]);
  /** the first chapter's headline is the page's <h1> */
  readonly lead = input(false);
  /** show the drag-to-rotate handle (the scene must be set up for orbiting) */
  readonly orbit = input(false);
  /** notifications from the scene */
  readonly stageEvent = output<FilmEvent>();

  /** optional extra content under a chapter's copy: `<ng-template #slideExtra let-chapter>` */
  protected readonly extra = contentChild<TemplateRef<{ $implicit: FilmChapter }>>('slideExtra');

  protected readonly hotspots = computed(() => this.chapters().flatMap((c) => c.hotspots));
  protected readonly length = computed(() => clock(this.chapters().reduce((s, c) => s + c.duration, 0)));

  protected readonly chapter = signal(0);
  protected readonly playing = signal(true);
  /** the 3D scene has rendered its first frame */
  protected readonly ready = signal(false);
  /** no WebGL — show the photograph instead */
  protected readonly fallback = signal(false);

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly bar = viewChild.required<ElementRef<HTMLElement>>('bar');
  private readonly timecode = viewChild.required<ElementRef<HTMLElement>>('timecode');

  private stage?: FilmStage;
  private readonly observers: IntersectionObserver[] = [];
  private shownSecond = -1;
  private destroyed = false;

  constructor() {
    afterNextRender(() => {
      // build the scene only when the film is about to scroll into view
      const near = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          near.disconnect();
          void this.boot();
        },
        { rootMargin: '700px 0px' },
      );
      near.observe(this.host().nativeElement);
      this.observers.push(near);
    });

    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      for (const o of this.observers) o.disconnect();
      this.stage?.dispose();
    });
  }

  /** Send a message to the scene (e.g. a colour the visitor picked). */
  send(name: string, value: number): void {
    this.stage?.receive(name, value);
  }

  protected toggle(): void {
    this.playing.update((p) => !p);
    this.stage?.setPlaying(this.playing());
  }

  protected goTo(index: number): void {
    this.stage?.goTo(index);
  }

  private async boot(): Promise<void> {
    const host = this.host().nativeElement;
    const spots = new Map<string, HTMLElement>();
    host.querySelectorAll<HTMLElement>('[data-spot]').forEach((el) => spots.set(el.dataset['spot']!, el));

    try {
      const Stage = await this.loader()();
      if (this.destroyed) return;
      this.stage = new Stage({
        canvas: this.canvas().nativeElement,
        host,
        spots,
        onChapter: (i) => this.chapter.set(i),
        onProgress: (p, elapsed) => this.onProgress(p, elapsed),
        onPlaying: (playing) => this.playing.set(playing),
        emit: (name, value) => this.stageEvent.emit({ name, value }),
      });
      await this.stage.ready;
      if (this.destroyed) return;
    } catch (err) {
      console.warn(`3D ${this.name()} film unavailable, showing the still image instead.`, err);
      this.fallback.set(true);
      return;
    }

    // `?hero=2:0.6` holds the hero film's chapter 3 at 60% — handy for posters and screenshots
    const frame = new URLSearchParams(location.search).get(this.name());
    if (frame) {
      const [c, p] = frame.split(':').map(Number);
      this.stage.freeze(c || 0, p || 0);
      this.playing.set(false);
    } else if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // respect the setting: open on a still (the end of the opening move); the visitor can press play
      this.stage.freeze(0, 0.9);
      this.playing.set(false);
    }

    this.ready.set(true);

    // only burn GPU while the film is actually on screen
    const visible = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.stage?.start() : this.stage?.stop()),
      { threshold: 0.05 },
    );
    visible.observe(host);
    this.observers.push(visible);
  }

  /** Runs every frame, so it writes straight to the DOM instead of through signals. */
  private onProgress(progress: number, elapsed: number): void {
    this.bar().nativeElement.style.setProperty('--progress', progress.toFixed(4));
    const second = Math.floor(elapsed);
    if (second !== this.shownSecond) {
      this.shownSecond = second;
      this.timecode().nativeElement.textContent = clock(elapsed);
    }
  }
}
